import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getCurrentLanguage } from '../search-trip/helpers'
import { calculateReservationTotal } from './calculateReservationTotal'
import { usePromotionCodes } from './promotions/usePromotionCodes'
import { checkUddsAvailability } from '../common/checkUddsAvailability'

export const STEPS = {
    PERSONAL_INFO: 'PERSONAL_INFO',
    PARTICIPANTS: 'PARTICIPANTS',
    INSURANCE: 'INSURANCE',
    PARKING: 'PARKING',
    PAYMENT: 'PAYMENT',
}
export const INSURENCE_VARIANT_TYPE = {
    REFERENCE: 'Reference',
    KOMFORT: 'Komfort',
    EXCELENT: 'Excelent',
}
export const PERSONAL_INFO_VIEW_TYPE = {
    LOGIN_FORM: 'login_form',
    FORM: 'form',
}
export const OFFER_COUNTRY_TYPE = {
    FOREIGN: 'zagraniczne',
    POLAND: 'Polska',
}

export const useReservation = () => {
    const [currentStep, setCurrentStep] = useState(STEPS.PERSONAL_INFO)
    const [personalInfoViewType, setPersonalInfoViewType] = useState(PERSONAL_INFO_VIEW_TYPE.LOGIN_FORM)
    const [adults, setAdults] = useState(0)
    const [children, setChildren] = useState(0)
    const [offer, setOffer] = useState([])
    const [userData, setUserData] = useState([])
    const [useCashbackForPay, setUseCashbackForPay] = useState(false)
    const [earnedCashbackAmount, setEarnedCashbackAmount] = useState(0)
    const [cashbackDisplayEnabled, setCashbackDisplayEnabled] = useState(false)
    const [cashbackRedemptionEnabled, setCashbackRedemptionEnabled] = useState(false)
    const [offerCountryType, setOfferCountryType] = useState(OFFER_COUNTRY_TYPE.FOREIGN)
    const [insuranceOffers, setInsuranceOffers] = useState(null)
    const [selectedInsurance, setSelectedInsurance] = useState(null)
    const [parkingAirports, setParkingAirports] = useState(null)
    const [parkingOffers, setParkingOffers] = useState(null)
    const [selectedParkingAirport, setSelectedParkingAirport] = useState(null)
    const [selectedParkingOffer, setSelectedParkingOffer] = useState(null)
    const [promotionCodes, setPromotionCodes] = useState([])
    const [promotionPreview, setPromotionPreview] = useState(null)
    const [showSearchBoxForParking, setShowSearchBoxForParking] = useState(false)
    const [loading, setLoading] = useState(true)
    const [reservationLoading, setReservationLoading] = useState(false)
    const [form, setForm] = useState({
        name: '',
        surname: '',
        address: '',
        email: '',
        repeatEmail: '',
        phone: '',
        postcode: '',
        city: '',
        birthdate: '',
        adults: [],
        children: [],
        agreements: [],
        invoiceBuildingNumber: '',
        invoiceCity: '',
        invoiceCountry: '',
        invoiceDisplayName: '',
        invoiceLocalNumber: '',
        invoicePostcode: '',
        invoiceStreet: '',
        invoiceNip: '',
        invoiceType: 'person',
    })
    const [masterCheckbox, setMasterCheckbox] = useState(false)
    const [errors, setErrors] = useState({})
    const [paymentMethod, setPaymentMethod] = useState(undefined)
    const { t, i18n } = useTranslation()
    const baseUrl = window.location.origin
    const [dataUdds, setDataUdds] = useState(null)
    const availabilityCheckRef = useRef(false)

    const handleChange = (e) => {
        const { name, value } = e.target

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleMasterCheckboxChange = () => {
        setMasterCheckbox((prevMasterCheckbox) => {
            setForm((prevForm) => ({
                ...prevForm,
                agreements: prevForm.agreements.map((agreement) => ({
                    ...agreement,
                    checked: !prevMasterCheckbox,
                })),
            }))

            return !prevMasterCheckbox
        })
    }

    const handleAdultChange = (index, field, value) => {
        setForm((prevForm) => ({
            ...prevForm,
            adults: prevForm.adults.map((adult, i) => (i === index ? { ...adult, [field]: value } : adult)),
        }))
    }

    const handleChildrenChange = (index, field, value) => {
        setForm((prevForm) => ({
            ...prevForm,
            children: prevForm.children.map((child, i) => (i === index ? { ...child, [field]: value } : child)),
        }))
    }

    const handleAgreementChange = (index, checkValue) => {
        setForm((prevForm) => ({
            ...prevForm,
            agreements: prevForm.agreements.map((agreement, i) =>
                i === index ? { ...agreement, checked: checkValue } : agreement,
            ),
        }))
    }

    const calculatePromotionsPreview = async (nextPromotionCodes) => {
        const pathParts = window.location.pathname.split('/')
        const offerHash = pathParts.filter(Boolean).pop() || ''
        const searchParams = new URLSearchParams(window.location.search)
        const allParams = Object.fromEntries(searchParams.entries())
        const payload = {
            offerHash,
            ...allParams,
            promotionCodes: nextPromotionCodes,
        }
        const endpointAttempts = [
            {
                url: `${baseUrl}/reservation/calculate_promotions`,
                body: payload,
            },
            {
                url: `${baseUrl}/reservation/check_reservation`,
                body: {
                    ...payload,
                    calculatePromotionsPreview: true,
                },
            },
        ]

        try {
            for (let i = 0; i < endpointAttempts.length; i++) {
                const attempt = endpointAttempts[i]
                const res = await fetch(attempt.url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(attempt.body),
                })

                if (!res.ok && (res.status === 404 || res.status === 405) && i < endpointAttempts.length - 1) {
                    continue
                }

                const json = await res.json()

                if (!res.ok || json.errors || !json.success) {
                    const message = (json.errors && json.errors[0]) || t('promotion-validation-error')

                    return {
                        status: false,
                        message,
                    }
                }

                return {
                    status: true,
                    promotions: json.promotions || null,
                }
            }

            return {
                status: false,
                message: t('promotion-validation-error'),
            }
        } catch (error) {
            return {
                status: false,
                message: t('promotion-validation-error'),
            }
        }
    }

    const {
        promotionCodeInput,
        setPromotionCodeInput,
        promotionError,
        addPromotionCode,
        removePromotionCode,
    } = usePromotionCodes({
        promotionCodes,
        calculatePreview: calculatePromotionsPreview,
        onChange: (
            nextPromotionCodes,
            nextPromotionPreview,
        ) => {
            setPromotionCodes(nextPromotionCodes)
            setPromotionPreview(nextPromotionPreview)
        },
        t,
    })

    const handleSubmit = async () => {
        try {
            setErrors({});
            setReservationLoading(true)
            const pathParts = window.location.pathname.split('/')
            const offerHash = pathParts[pathParts.length - 1]
            const searchParams = new URLSearchParams(window.location.search)
            const allParams = Object.fromEntries(searchParams.entries())
            const baseUrl = window.location.origin

            const res = await fetch(`${baseUrl}/reservation/make`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    step: currentStep,
                    offerHash,
                    ...allParams,
                    offer,
                    selectedInsurance,
                    selectedParkingOffer,
                    insuranceOffers,
                    payment_method: paymentMethod,
                    useCashbackForPay: cashbackRedemptionEnabled && useCashbackForPay,
                    promotionCodes,
                    earnedCashbackAmount,
                    ...form,
                }),
            })

            const json = await res.json()

            if (json.errors || !json.success) {
                window.scrollTo(0, 0)
                setErrors(json.errors)

                return
            }

            window.location.href = json.paymentUrl
        } catch (error) {
            console.error('Error validation data:', error)
        } finally {
            setReservationLoading(false)
        }
    }

    const buildListingUrl = () => {
        const source = new URLSearchParams(window.location.search)
        const params = new URLSearchParams()

        const preservedKeys = [
            'od',
            'do',
            'od-max',
            'dorosli',
            'dzieci',
            'kierunek',
            'transport',
            'z',
            'min-dni',
            'max-dni',
        ]

        preservedKeys.forEach((key) => {
            const value = source.get(key)

            if (value !== null && value !== '') {
                params.set(key, value)
            }
        })

        if (!params.has('kierunek') && offer.country) {
            params.set('kierunek', offer.country)
        }

        if (!params.has('transport')) {
            const transportMap = {
                F: 'samolot',
                B: 'autokar',
                H: 'wlasny',
            }

            const transport = transportMap[offer.transport]

            if (transport) {
                params.set('transport', transport)
            }
        }

        // The listing expects operators as an array.
        const operators = source.getAll('operator[]')
        const operator = source.get('operator')

        if (operators.length > 0) {
            operators.forEach((value) => {
                if (value) {
                    params.append('operator[]', value)
                }
            })
        } else if (operator) {
            params.append('operator[]', operator)
        }

        const query = params.toString()

        return `/wycieczki${query ? `?${query}` : ''}`
    }

    const nextStep = async () => {
        if (availabilityCheckRef.current) {
            return
        }

        availabilityCheckRef.current = true

        try {
            const available = await checkUddsAvailability(window.location.href)

            if (!available) {
                window.location.replace(buildListingUrl())
                return
            }

            await checkReservation()
        } catch (error) {
            console.error('Availability check failed:', error)
        } finally {
            availabilityCheckRef.current = false
        }
    }

    const checkReservation = async () => {
        try {
            setErrors({});
            const baseUrl = window.location.origin
            const pathParts = window.location.pathname.split('/')
            const offerHash = pathParts[pathParts.length - 1]
            const res = await fetch(`${baseUrl}/reservation/check_reservation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    step: currentStep,
                    offerHash,
                    adults: adults,
                    children: children,
                    payment_method: paymentMethod,
                    ...form,
                }),
            })

            const json = await res.json()
            window.scrollTo(0, 0)


            //todo: support non 200 response - 422
            if (json.errors) {
                setErrors(json.errors)

                return
            }

            setErrors({})
            switch (currentStep) {
                case STEPS.PERSONAL_INFO:
                    setCurrentStep(STEPS.PARTICIPANTS)
                    break
                case STEPS.PARTICIPANTS:
                    if (offerCountryType === OFFER_COUNTRY_TYPE.POLAND) {
                        setCurrentStep(STEPS.PAYMENT)
                    } else {
                        setCurrentStep(STEPS.INSURANCE)
                    }

                    break
                case STEPS.INSURANCE:
                    if (!(offer.length > 0) || (offer.charterType && offer.tripDirection === 'oneWay')) {
                        setCurrentStep(STEPS.PAYMENT)
                    } else {
                        setCurrentStep(STEPS.PARKING)
                    }
                    break
                case STEPS.PARKING:
                    setCurrentStep(STEPS.PAYMENT)
                    break
                default:
                    break
            }
        } catch (error) {
            console.error('Error validation data:', error)
        }
    }

    const getInsurances = async () => {
        try {
            const baseUrl = window.location.origin
            const pathParts = window.location.pathname.split('/')
            const searchParams = new URLSearchParams(window.location.search)
            const allParams = Object.fromEntries(searchParams.entries())
            const offerHash = pathParts[pathParts.length - 1]
            const res = await fetch(`${baseUrl}/reservation/get_insurances`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    step: currentStep,
                    ...allParams,
                    offerHash,
                    adults: adults,
                    children: children,
                    ...form,
                }),
            })

            const json = await res.json()

            //todo: support non 200 response - 422
            if (json.errors) {
                setErrors(json.errors)

                return
            }

            setErrors({});
            setInsuranceOffers(json)
            setSelectedInsurance(json.calculations[1])
        } catch (error) {
            console.error('Error validation data:', error)
        }
    }

    const getParkingAirports = async () => {
        try {
            const baseUrl = window.location.origin
            const pathParts = window.location.pathname.split('/')
            const searchParams = new URLSearchParams(window.location.search)
            const allParams = Object.fromEntries(searchParams.entries())
            const offerHash = pathParts[pathParts.length - 1]
            const res = await fetch(`${baseUrl}/reservation/get_parking_airports`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    step: currentStep,
                    ...allParams,
                    offer,
                    offerHash,
                    adults: adults,
                    children: children,
                    ...form,
                }),
            })

            const json = await res.json()

            //todo: support non 200 response - 422
            if (json.errors) {
                setErrors(json.errors)

                return
            }

            const selectedAirportObject = json.find(
                (airport) => airport.selected === true
            );

            setParkingAirports(json)

            if (selectedAirportObject) {
                setSelectedParkingAirport(selectedAirportObject.id);
                getParkingOffers(selectedAirportObject.id);
            } else {
                setShowSearchBoxForParking(true);
            }

        } catch (error) {
            console.error('Error validation data:', error)
        }
    }

    const getParkingOffers = async (selectedAirport) => {
        try {
            if (!selectedAirport) {
                setParkingOffers([]);

                return
            }

            setSelectedParkingOffer(null);

            const baseUrl = window.location.origin
            const pathParts = window.location.pathname.split('/')
            const searchParams = new URLSearchParams(window.location.search)
            const allParams = Object.fromEntries(searchParams.entries())
            const offerHash = pathParts[pathParts.length - 1]
            const res = await fetch(`${baseUrl}/reservation/get_parking_offers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    step: currentStep,
                    ...allParams,
                    offerHash,
                    adults: adults,
                    children: children,
                    selectedAirportId: selectedAirport,
                    ...form,
                }),
            })

            const json = await res.json()

            if (json.errors) {
                setErrors(json.errors)

                return
            }

            setParkingOffers(json)
        } catch (error) {
            console.error('Error validation data:', error)
        }
    }

    const getReservationTotalPrice = (includeCashbackDiscount = false) => calculateReservationTotal(
            {
                offer,
                promotionPreview,
                selectedInsurance,
                selectedParkingOffer,
                userData,
                useCashbackForPay,
            },
            includeCashbackDiscount,
        )

    const fetchOfferData = async () => {
        const pathParts = window.location.pathname.split('/')
        const offerHash = pathParts[pathParts.length - 1]
        const searchParams = new URLSearchParams(window.location.search)
        const allParams = Object.fromEntries(searchParams.entries())
        const adultCounter = allParams['dorosli'] ? allParams['dorosli'] : 0
        const childrenCounter = String(allParams.dzieci || '')
            .split(',')
            .map((age) => age.trim())
            .filter((age) => age !== '')
            .length

        setAdults(adultCounter)
        setChildren(childrenCounter)

        try {
            const res = await fetch(`${baseUrl}/api_reservation/get_offer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ offerHash, ...allParams }),
            })
            const json = await res.json()

            const cashback = json.cashback || {}
            const displayEnabled = cashback.displayEnabled === true
            const redemptionEnabled = cashback.redemptionEnabled === true

            setCashbackDisplayEnabled(displayEnabled)
            setCashbackRedemptionEnabled(redemptionEnabled)

            if (!redemptionEnabled) {
                setUseCashbackForPay(false)
            }

            if (!json.offer) {
                setOffer({})
            } else {
                setOffer(json.offer)
                setDataUdds(json.dataUdds || null)

                if (json.userData && Object.keys(json.userData).length > 0) {
                    setPersonalInfoViewType(PERSONAL_INFO_VIEW_TYPE.FORM)
                    setForm((prevForm) => ({
                        ...prevForm,
                        name: json.userData.name ? json.userData.name : '',
                        surname: json.userData.surname ? json.userData.surname : '',
                        address: json.userData.address ? json.userData.address : '',
                        email: json.userData.email ? json.userData.email : '',
                        repeatEmail: json.userData.email ? json.userData.email : '',
                        postcode: json.userData.postcode ? json.userData.postcode : '',

                        invoiceType: json.userData.invoiceType || prevForm.invoiceType,
                        invoiceDisplayName: json.userData.invoiceDisplayName ? json.userData.invoiceDisplayName : '',
                        invoiceNip: json.userData.invoiceNip ? json.userData.invoiceNip : '',
                        invoiceStreet: json.userData.invoiceStreet ? json.userData.invoiceStreet : '',
                        invoiceBuildingNumber: json.userData.invoiceBuildingNumber ? json.userData.invoiceBuildingNumber : '',
                        invoiceLocalNumber: json.userData.invoiceLocalNumber ? json.userData.invoiceLocalNumber : '',
                        invoiceCity: json.userData.invoiceCity ? json.userData.invoiceCity : '',
                        invoicePostcode: json.userData.invoicePostcode ? json.userData.invoicePostcode : '',
                        invoiceCountry: json.userData.invoiceCountry ? json.userData.invoiceCountry : '',
                    }))
                    setUserData(json.userData)
                } else {
                    //setUserData({})
                }

                setEarnedCashbackAmount(
                    displayEnabled && Number(json.earnedCashbackAmount) > 0
                        ? Number(json.earnedCashbackAmount)
                        : 0
                )

                const initialAdults = Array.from({ length: adultCounter }, (_, i) => ({
                    name: '',
                    surname: '',
                    birthdate: '',
                    gender: 'M',
                }))
                const initialChildren = Array.from({ length: childrenCounter }, (_, i) => ({
                    name: '',
                    surname: '',
                    birthdate: '',
                    gender: 'M',
                }))
                setForm((prevForm) => ({
                    ...prevForm,
                    adults: initialAdults,
                    children: initialChildren,
                    agreements: json.agreements ? json.agreements : [],
                }))

                if (json.offer.country && json.offer.country === 'Polska') {
                    setOfferCountryType(OFFER_COUNTRY_TYPE.POLAND);
                } else {
                    setOfferCountryType(OFFER_COUNTRY_TYPE.FOREIGN);
                }

            }
        } catch (error) {
            console.error('Error fetching data:', error)
        }
        setLoading(false)
    }

    useEffect(() => {
        fetchOfferData()
    }, [])

    useEffect(() => {
        const currentLang = getCurrentLanguage()

        i18n.changeLanguage(currentLang).then(r => null)
    }, [i18n])

    const stepReservationDescription =
        currentStep === STEPS.PERSONAL_INFO
            ? t('go-to-participants')
            : currentStep === STEPS.PARTICIPANTS
              ? t('go-to-payment')
              : t('pay-selected-method')

    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const formattedToday = `${yyyy}-${mm}-${dd}`

    const minDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())

    const year = minDate.getFullYear()
    const month = (minDate.getMonth() + 1).toString().padStart(2, '0')
    const day = minDate.getDate().toString().padStart(2, '0')

    const formattedMinDate = `${year}-${month}-${day}`

    return {
        loading,
        offer,
        adults,
        children,
        currentStep,
        form,
        handleChange,
        handleChildrenChange,
        insuranceOffers,
        parkingAirports,
        parkingOffers,
        selectedInsurance,
        setSelectedInsurance,
        selectedParkingAirport,
        setSelectedParkingAirport,
        selectedParkingOffer,
        setSelectedParkingOffer,
        showSearchBoxForParking,
        getInsurances,
        getParkingAirports,
        getParkingOffers,
        errors,
        paymentMethod,
        setPaymentMethod,
        stepReservationDescription,
        nextStep,
        handleSubmit,
        reservationLoading,
        handleAdultChange,
        masterCheckbox,
        handleMasterCheckboxChange,
        handleAgreementChange,
        formattedToday,
        formattedMinDate,
        getReservationTotalPrice,
        offerCountryType,
        personalInfoViewType,
        setPersonalInfoViewType,
        userData,
        useCashbackForPay,
        setUseCashbackForPay,
        earnedCashbackAmount,
        cashbackDisplayEnabled,
        cashbackRedemptionEnabled,
        promotionCodeInput,
        setPromotionCodeInput,
        promotionCodes,
        promotionPreview,
        promotionError,
        addPromotionCode,
        removePromotionCode,
        dataUdds,
    }
}
